import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  TextField,
  Chip,
  Divider,
  Paper,
  useMediaQuery,
  useTheme,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  InputAdornment,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Article as ArticleIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  Sell as SellIcon,
  Visibility as ViewIcon,
  AttachMoney as MoneyIcon,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  createPost,
  updatePost,
  deletePost,
  searchPosts,
  getAllPosts,
} from "../../redux/postSlice";
import { Post, CreatePostDto } from "../../models/Post";

// Get width from environment variable with fallback
const MOBILE_APP_WIDTH = parseInt(
  process.env.REACT_APP_MOBILE_APP_WIDTH || "500"
);

interface SellPostData {
  price: number;
  currency: string;
  isForSale: boolean;
}

const PostsSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${MOBILE_APP_WIDTH}px)`);
  const { posts, searchResults, loading, error } = useAppSelector(
    (state) => state.posts
  );
  const [isEditing, setIsEditing] = useState(false);
  const [currentPost, setCurrentPost] = useState<Partial<Post>>({
    title: "",
    content: "",
    shortVersion: "",
    isPublished: false,
    isForSale: false,
    price: 0,
    currency: "USD",
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sellDialogOpen, setSellDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const dispatch = useAppDispatch();

  // Load posts on component mount
  useEffect(() => {
    dispatch(getAllPosts()).unwrap();
  }, [dispatch]);

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchQuery);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchQuery]);

  // Trigger search when debounced term changes
  useEffect(() => {
    if (debouncedSearchTerm) {
      dispatch(searchPosts({ keyword: debouncedSearchTerm }));
    }
  }, [debouncedSearchTerm, dispatch]);

  const handleOpenAddForm = () => {
    setIsEditing(false);
    setCurrentPost({
      title: "",
      content: "",
      shortVersion: "",
      isPublished: false,
      isForSale: false,
      price: 0,
      currency: "USD",
    });
    setShowForm(true);
  };

  const handleOpenEditForm = (post: Post) => {
    setIsEditing(true);
    setCurrentPost(post);
    setEditId(post.id);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setCurrentPost({
      title: "",
      content: "",
      shortVersion: "",
      isPublished: false,
      isForSale: false,
      price: 0,
      currency: "USD",
    });
    setEditId(null);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setCurrentPost((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setCurrentPost((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSavePost = async () => {
    if (!currentPost.title || !currentPost.content) {
      return;
    }

    try {
      if (isEditing && editId) {
        await dispatch(
          updatePost({
            id: editId,
            title: currentPost.title,
            content: currentPost.content,
            shortVersion: currentPost.shortVersion || "",
            isPublished: currentPost.isPublished || false,
            isForSale: currentPost.isForSale || false,
            price: currentPost.price || 0,
            currency: currentPost.currency || "USD",
            creationTime: "",
          })
        ).unwrap();
      } else {
        await dispatch(
          createPost({
            title: currentPost.title,
            content: currentPost.content,
            shortVersion: currentPost.shortVersion || "",
            isPublished: currentPost.isPublished || false,
            isForSale: currentPost.isForSale || false,
            price: currentPost.price || 0,
            currency: currentPost.currency || "USD",
          } as CreatePostDto)
        ).unwrap();
      }

      handleCloseForm();
      // Refresh the posts list
      dispatch(getAllPosts());
    } catch (error) {
      console.error("Error saving post:", error);
    }
  };

  const handleDeletePost = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this post?")) {
      try {
        await dispatch(deletePost(id)).unwrap();
        // Refresh the posts list
        dispatch(getAllPosts());
      } catch (error) {
        console.error("Error deleting post:", error);
      }
    }
  };

  const handlePublishToggle = async (post: Post) => {
    try {
      await dispatch(
        updatePost({
          ...post,
          isPublished: !post.isPublished,
        })
      ).unwrap();
      // Refresh the posts list
      dispatch(getAllPosts());
    } catch (error) {
      console.error("Error updating post:", error);
    }
  };

  const handleSellToggle = async (post: Post) => {
    try {
      await dispatch(
        updatePost({
          ...post,
          isForSale: !post.isForSale,
        })
      ).unwrap();
      // Refresh the posts list
      dispatch(getAllPosts());
    } catch (error) {
      console.error("Error updating post:", error);
    }
  };

  const handleBuyPost = (post: Post) => {
    setSelectedPost(post);
    setSellDialogOpen(true);
    setActiveStep(0);
  };

  const handleViewPost = (post: Post) => {
    setSelectedPost(post);
    setViewDialogOpen(true);
  };

  const handleCloseDialogs = () => {
    setSellDialogOpen(false);
    setViewDialogOpen(false);
    setSelectedPost(null);
    setActiveStep(0);
  };

  const handlePurchase = async () => {
    // Simulate purchase process
    setActiveStep(1);

    // Simulate API call
    setTimeout(() => {
      setActiveStep(2);
    }, 2000);
  };

  const scrollableBoxStyles = {
    maxHeight: isMobile ? "50vh" : "60vh",
    overflowY: "auto",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
    WebkitOverflowScrolling: "touch",
  };

  const filteredPosts = posts.filter(
    (post) =>
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.shortVersion?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Format price for display
  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(price);
  };

  if (loading && posts.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper
      sx={{
        p: isMobile ? 1 : 2,
        width: "100%",
        maxWidth: MOBILE_APP_WIDTH,
        margin: "auto",
        ...(isMobile && {
          maxWidth: "100%",
          borderRadius: 0,
          boxShadow: "none",
        }),
        ...(!isMobile && {
          borderRadius: 2,
          boxShadow: 3,
        }),
      }}
    >
      {/* {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {typeof error === "object" && error !== null && "message" in error
            ? (error as { message: string }).message
            : String(error)}
        </Alert>
      )} */}

      {showForm ? (
        <Paper elevation={isMobile ? 0 : 3} sx={{ p: isMobile ? 2 : 3, mb: 2 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <IconButton
              onClick={handleCloseForm}
              sx={{ mr: 1 }}
              size={isMobile ? "small" : "medium"}
            >
              <ArrowBackIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
            <Typography
              variant="h6"
              sx={{ fontSize: isMobile ? "1.25rem" : "1.5rem" }}
            >
              {isEditing ? "Edit Post" : "Add New"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Post Title"
              name="title"
              value={currentPost.title}
              onChange={handleInputChange}
              size={isMobile ? "small" : "medium"}
              required
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Short Version/Preview"
              name="shortVersion"
              value={currentPost.shortVersion}
              onChange={handleInputChange}
              size={isMobile ? "small" : "medium"}
              placeholder="Brief summary of your post that will be visible to all users..."
              helperText="This will be shown to users before they purchase or view the full post"
            />

            <TextField
              fullWidth
              multiline
              rows={6}
              label="Full Content (HTML)"
              name="content"
              value={currentPost.content}
              onChange={handleInputChange}
              size={isMobile ? "small" : "medium"}
              required
              placeholder="Enter your full post content with HTML formatting..."
              helperText="You can use basic HTML tags: p, br, strong, em, a, ul, ol, li, h1-h3"
            />

            <Box
              display="flex"
              gap={2}
              flexDirection={isMobile ? "column" : "row"}
            >
              <FormControlLabel
                control={
                  <Switch
                    name="isPublished"
                    checked={currentPost.isPublished || false}
                    onChange={handleInputChange}
                    size={isMobile ? "small" : "medium"}
                  />
                }
                label="Publish immediately"
                sx={{ flex: 1 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    name="isForSale"
                    checked={currentPost.isForSale || false}
                    onChange={handleInputChange}
                    size={isMobile ? "small" : "medium"}
                  />
                }
                label="Sell this post"
                sx={{ flex: 1 }}
              />
            </Box>

            {currentPost.isForSale && (
              <Box
                display="flex"
                gap={2}
                flexDirection={isMobile ? "column" : "row"}
              >
                <TextField
                  label="Price"
                  name="price"
                  type="number"
                  value={currentPost.price}
                  onChange={handleInputChange}
                  size={isMobile ? "small" : "medium"}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  sx={{ flex: 1 }}
                />

                {/* <TextField
                  select
                  label="Currency"
                  name="currency"
                  value={currentPost.currency || "USD"}
                  onChange={handleInputChange}
                  size={isMobile ? "small" : "medium"}
                  sx={{ flex: 1 }}
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                </TextField> */}
              </Box>
            )}
          </Box>

          <Box display="flex" justifyContent="flex-end" mt={2} gap={1}>
            <Button
              onClick={handleCloseForm}
              startIcon={<CloseIcon fontSize={isMobile ? "small" : "medium"} />}
              size={isMobile ? "small" : "medium"}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSavePost}
              color="primary"
              variant="contained"
              startIcon={<CheckIcon fontSize={isMobile ? "small" : "medium"} />}
              disabled={!currentPost.title || !currentPost.content || loading}
              size={isMobile ? "small" : "medium"}
            >
              {loading ? "Saving..." : isEditing ? "Update" : "Create"}
            </Button>
          </Box>
        </Paper>
      ) : (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            flexDirection={isMobile ? "column" : "row"}
            gap={isMobile ? 2 : 0}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: isMobile ? "1.25rem" : "1.5rem", paddingLeft: 2 }}
            >
              Manage Posts ({posts.length})
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={<AddIcon fontSize={isMobile ? "small" : "medium"} />}
                onClick={handleOpenAddForm}
                size={isMobile ? "small" : "medium"}
              >
                {isMobile ? "New Post" : "Add New"}
              </Button>
            </Box>
          </Box>

          <Box sx={{ p: isMobile ? 1 : 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search posts by title, content, or short version..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
              size={isMobile ? "small" : "medium"}
            />
          </Box>

          <Paper elevation={isMobile ? 0 : 2} sx={scrollableBoxStyles}>
            <List>
              {filteredPosts.length > 0 ? (
                filteredPosts.map((post) => (
                  <React.Fragment key={post.id}>
                    <ListItem
                      secondaryAction={
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          flexDirection={isMobile ? "column" : "row"}
                        >
                          {/* <FormControlLabel
                            control={
                              <Switch
                                checked={post.isPublished}
                                onChange={() => handlePublishToggle(post)}
                                size="small"
                                color="success"
                              />
                            }
                            label={post.isPublished ? "Published" : "Draft"}
                            labelPlacement="start"
                          /> */}
                          {/* <FormControlLabel
                            control={
                              <Switch
                                checked={post.isForSale || false}
                                onChange={() => handleSellToggle(post)}
                                size="small"
                                color="secondary"
                              />
                            }
                            label="For Sale"
                            labelPlacement="start"
                          /> */}
                          <IconButton
                            edge="end"
                            onClick={() => handleOpenEditForm(post)}
                            size={isMobile ? "small" : "medium"}
                            color="primary"
                          >
                            <EditIcon
                              fontSize={isMobile ? "small" : "medium"}
                            />
                          </IconButton>
                          <IconButton
                            edge="end"
                            onClick={() => handleDeletePost(post.id)}
                            size={isMobile ? "small" : "medium"}
                            color="error"
                          >
                            <DeleteIcon
                              fontSize={isMobile ? "small" : "medium"}
                            />
                          </IconButton>
                        </Box>
                      }
                      sx={{
                        py: isMobile ? 1 : 1.5,
                        alignItems: "flex-start",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                          width: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                            minWidth: 0,
                            flex: 1,
                          }}
                        >
                          <ArticleIcon
                            color={post.isPublished ? "success" : "disabled"}
                            fontSize={isMobile ? "small" : "medium"}
                          />
                          <ListItemText
                            primary={
                              <Box
                                display="flex"
                                alignItems="center"
                                flexWrap="wrap"
                                gap={1}
                              >
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontSize: isMobile ? "0.875rem" : "1rem",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {post.title}
                                </Typography>
                                <Chip
                                  icon={
                                    post.isPublished ? (
                                      <PublicIcon />
                                    ) : (
                                      <LockIcon />
                                    )
                                  }
                                  label={
                                    post.isPublished ? "Published" : "Draft"
                                  }
                                  size="small"
                                  color={
                                    post.isPublished ? "success" : "default"
                                  }
                                  variant={
                                    post.isPublished ? "filled" : "outlined"
                                  }
                                />
                                {post.isForSale && (
                                  <Chip
                                    icon={<SellIcon />}
                                    label={formatPrice(
                                      post.price || 0,
                                      post.currency || "USD"
                                    )}
                                    size="small"
                                    color="secondary"
                                    variant="filled"
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                {post.shortVersion && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: isMobile
                                        ? "0.75rem"
                                        : "0.875rem",
                                      color: "text.secondary",
                                      mb: 1,
                                      fontStyle: "italic",
                                    }}
                                  >
                                    {post.shortVersion}
                                  </Typography>
                                )}
                                <Box
                                  display="flex"
                                  gap={2}
                                  flexWrap="wrap"
                                  alignItems="center"
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    By {post.creatorUserName || "Unknown"}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    {formatDate(post.creationTime)}
                                  </Typography>
                                  {/*
                                  {post.isPublished && (
                                    <Box display="flex" gap={1}>
                                      {post.isForSale ? (
                                        <Button
                                          variant="contained"
                                          size="small"
                                          startIcon={<MoneyIcon />}
                                          onClick={() => handleBuyPost(post)}
                                          color="secondary"
                                        >
                                          Buy (
                                          {formatPrice(
                                            post.price || 0,
                                            post.currency || "USD"
                                          )}
                                          )
                                        </Button>
                                      ) : (
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          startIcon={<ViewIcon />}
                                          onClick={() => handleViewPost(post)}
                                        >
                                          View Article
                                        </Button>
                                      )}
                                    </Box>
                                  )}*/}
                                </Box>
                              </Box>
                            }
                            sx={{ my: 0 }}
                          />
                        </Box>
                      </Box>
                    </ListItem>
                    <Divider variant="inset" component="li" />
                  </React.Fragment>
                ))
              ) : (
                <Typography
                  variant="body2"
                  sx={{ p: 3, textAlign: "center", color: "text.secondary" }}
                >
                  {searchQuery
                    ? `No posts found matching "${searchQuery}"`
                    : "No posts found. Create your first post!"}
                </Typography>
              )}
            </List>
          </Paper>
        </>
      )}

      {/* Buy Post Dialog */}
      <Dialog
        open={sellDialogOpen}
        onClose={handleCloseDialogs}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <MoneyIcon color="secondary" />
            Purchase Post
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} orientation="vertical">
            <Step>
              <StepLabel>Post Details</StepLabel>
              <StepContent>
                {selectedPost && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {selectedPost.title}
                    </Typography>
                    {selectedPost.shortVersion && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {selectedPost.shortVersion}
                      </Typography>
                    )}
                    <Typography variant="body1" gutterBottom>
                      Price:{" "}
                      <strong>
                        {formatPrice(
                          selectedPost.price || 0,
                          selectedPost.currency || "USD"
                        )}
                      </strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Author: {selectedPost.creatorUserName}
                    </Typography>
                  </Box>
                )}
              </StepContent>
            </Step>
            <Step>
              <StepLabel>Processing Payment</StepLabel>
              <StepContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <CircularProgress size={20} />
                  <Typography>Completing your purchase...</Typography>
                </Box>
              </StepContent>
            </Step>
            <Step>
              <StepLabel>Purchase Complete</StepLabel>
              <StepContent>
                <Typography>
                  Thank you for your purchase! You now have access to the full
                  content.
                </Typography>
              </StepContent>
            </Step>
          </Stepper>
        </DialogContent>
        <DialogActions>
          {activeStep === 0 && (
            <>
              <Button onClick={handleCloseDialogs}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handlePurchase}
                startIcon={<MoneyIcon />}
              >
                Purchase Now
              </Button>
            </>
          )}
          {activeStep === 2 && (
            <Button variant="contained" onClick={handleCloseDialogs}>
              View Content
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* View Post Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseDialogs}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedPost?.title}</DialogTitle>
        <DialogContent>
          {selectedPost && (
            <Box>
              <Box display="flex" gap={2} alignItems="center" mb={2}>
                <Typography variant="body2" color="text.secondary">
                  By {selectedPost.creatorUserName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(selectedPost.creationTime)}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box
                dangerouslySetInnerHTML={{ __html: selectedPost.content }}
                sx={{
                  "& h1, & h2, & h3": { mt: 2, mb: 1 },
                  "& p": { mb: 1 },
                  "& ul, & ol": { pl: 2 },
                  "& li": { mb: 0.5 },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PostsSection;
